/**
 * Base class for all captcha drivers
 * Provides common interface and hooks for driver implementations
 */
class BaseDriver {
  constructor(config) {
    this.config = config // { name, public_key, can_render }
  }

  /**
   * Initialize driver (load scripts, set up listeners)
   * @returns {Promise<void>}
   */
  async init() {
    await this.waitForLibrary()
    this.onLibraryReady()
  }

  /**
   * Wait for vendor library to load
   * MUST be implemented by subclasses
   * @returns {Promise<void>}
   */
  async waitForLibrary() {
    throw new Error('Must implement waitForLibrary()')
  }

  /**
   * Called when library is ready
   * Optional hook for subclasses
   */
  onLibraryReady() {
    // Optional hook
  }

  /**
   * Get token for verification
   * MUST be implemented by subclasses
   * @param {string} action - Action name (e.g., 'submit', 'login')
   * @param {{ container?: HTMLElement | null }} options
   * @returns {Promise<string|null>}
   */
  async getToken(action, options = {}) {
    throw new Error('Must implement getToken()')
  }

  /**
   * Find the rendered captcha response input inside a container.
   * @param {HTMLElement | null} container
   * @returns {HTMLInputElement|HTMLTextAreaElement|null}
   */
  findTokenInput(container = document.body) {
    if (!container) {
      return null
    }

    if (container.matches?.('[name="g-recaptcha-response"]')) {
      return container
    }

    return container.querySelector?.('[name="g-recaptcha-response"]') || null
  }

  /**
   * Read the rendered captcha token from the DOM.
   * @param {HTMLElement | null} container
   * @returns {string|null}
   */
  getRenderedToken(container = document.body) {
    const tokenInput = this.findTokenInput(container)
    return tokenInput?.value || null
  }

  /**
   * Watch DOM for elements to render
   * Only renderable drivers (V2, Turnstile) implement this
   * @param {HTMLElement} container - Container to watch
   */
  watchForElements(container) {
    // Only renderable drivers implement this
  }

  /**
   * Check if this driver can render visible elements
   * @returns {boolean}
   */
  get canRender() {
    return this.config.can_render
  }

  /**
   * Get the public key for this driver
   * @returns {string}
   */
  get publicKey() {
    return this.config.public_key
  }
}

export default BaseDriver
